import React, { useState, useEffect } from "react";
import { Target, PieChart, Sparkles, Star } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  MOCKUP IMAGE with placeholder fallback                             */
/* ------------------------------------------------------------------ */
function MockupImage({ src, alt, className, style }: { src: string; alt: string; className?: string; style?: React.CSSProperties }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={`aspect-[9/19] rounded-[2.5rem] bg-muted flex items-center justify-center ${className ?? ''}`}
        style={{ boxShadow: '0 40px 80px rgba(0,0,0,0.12)', ...style }}
      >
        <span className="text-xs text-muted-foreground">App mockup coming</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={() => setFailed(true)}
    />
  );
}
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SEO from "@/components/SEO";
import { ArrowRight, Check, ChevronDown, X } from "lucide-react";
import { Link } from "react-router-dom";
import { APP_NAME } from "@/lib/branding";

/* ------------------------------------------------------------------ */
/*  DATA                                                               */
/* ------------------------------------------------------------------ */

const FEATURES = [
  { Icon: Target, iconBg: '#f0fdf4', iconColor: '#16a34a', title: 'Money Stage System', body: "Take a short quiz. Get placed in your personal money stage. Unlock guidance, priorities, and next steps built specifically for where you are — not where someone thinks you should be." },
  { Icon: PieChart, iconBg: '#eff6ff', iconColor: '#2563eb', title: 'Wants / Needs / Freedom™ Clarity Engine', body: "Every transaction categorised into Wants, Needs, or Freedom™. Visual dashboards that show, at a glance, whether your money is going where you say it matters." },
  { Icon: Sparkles, iconBg: '#faf5ff', iconColor: '#9333ea', title: 'AI Insights That Feel Human', body: "One gentle nudge per week. No data dumps, no jargon. Your AI spots patterns, flags what matters, and writes you a monthly reflection report — like a smart friend who’s looked at your numbers so you don’t have to." },
  { Icon: Star, iconBg: '#fff1f2', iconColor: '#e11d48', title: 'Goals & Wishlist Tracker', body: "Emergency fund. First home. Debt-free. Investing. Connect your goals to your daily spending with live progress bars and realistic timelines — so you always know if you’re on track." },
];


const BUILT_FOR_YOU = [
<>You earn decent money but never quite feel <em>on top of</em> it</>,
"You've tried budgeting apps before — and dropped them after a few weeks, feeling guilty",
"You feel anxious when you think about money, so you try not to think about it",
"You know you should be doing more — you just don't know what to focus on, or where to start",
<>You want a system built around <em>your</em> life — not a spreadsheet someone else designed</>];


const EARLY_ACCESS_PERKS = [
<><strong>£27/year — locked in for life.</strong> Never changes, never expires, no matter what we charge in future.</>,
<><strong>First access when the app goes live</strong> — before the public.</>,
<><strong>Early access to the Money Stage Quiz</strong> — find out exactly where you are before anyone else.</>,
<><strong>A personal note from Raluca</strong> — the mindset shift that changed her relationship with money (not what most people expect).</>];


const HOW_IT_WORKS_STEPS = [
{
  num: "01",
  title: "You join in 10 seconds",
  body: "Just your email. That's it. No card, no commitment."
},
{
  num: "02",
  title: "You get a confirmation from Raluca",
  body: "A personal welcome, a sneak peek at what's coming, and your founding member status confirmed."
},
{
  num: "03",
  title: "We notify you the moment we go live",
  body: "On 11 November 2026, you're in before the public — and your £27/year founding rate is locked."
},
{
  num: "04",
  title: "You get 7 days of free app access",
  body: "Explore the app without entering payment details. If you choose to upgrade, your founding member annual subscription is charged in full at checkout."
}];


const FAQ_ITEMS = [
{
  q: "Is it really free to join the waitlist?",
  a: "Yes — completely free. Joining the waitlist costs nothing and commits you to nothing. You'll only be asked for payment if you choose to activate your account when the app launches."
},
{
  q: "Will I be bombarded with emails?",
  a: "No. You'll get a confirmation when you sign up, a heads-up when we launch, and the occasional update if something meaningful is happening. That's it."
},
{
  q: "What's a founding member — and why does it matter?",
  a: "The first 100 women to join are founding members. You lock in £27/year for life — we will never offer that price again, to anyone. After 100 spots, the price moves to £47/year. Being early has a real, permanent benefit here."
},
{
  q: "When does the app launch?",
  a: "11 November 2026. Waitlist members get access first — we'll notify you the moment the doors open."
},
{
  q: "What if it's not for me?",
  a: "No hard feelings. You can use the app for 7 days without entering payment details. If you choose to upgrade, your annual subscription is charged in full at checkout."
}];


const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a }
  }))
};

/* ------------------------------------------------------------------ */
/*  WAITLIST FORM                                                      */
/* ------------------------------------------------------------------ */

function WaitlistForm({ className = "", buttonText = "Join the waitlist", onSuccess, variant = 'dark' }: {className?: string;buttonText?: string;onSuccess?: () => void;variant?: 'dark' | 'light';}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus("loading");
    setErrorMsg("");

    const { error } = await supabase.
    from("waitlist_signups" as any).
    insert({ email: email.trim().toLowerCase() } as any);

    if (error) {
      if (error.code === "23505") {
        setStatus("success");
      onSuccess?.();
      } else {
        setStatus("error");
        setErrorMsg("Something went wrong. Please try again.");
      }
    } else {
      setStatus("success");
      onSuccess?.();
    }
  };

  if (status === "success") {
    return (
      <div className={`rounded-2xl p-6 shadow-sm ${className}`} style={{ backgroundColor: "#ffffff" }}>
        <p className="text-lg font-semibold" style={{ color: "#111" }}>
          You're in. ✨
        </p>
        <p className="mt-2 leading-relaxed" style={{ color: "#888" }}>
          Your founding spot is reserved — £27/year, locked in for life. Check your inbox for a personal note from Raluca, and keep an eye out for launch day — you'll be first through the door.
        </p>
      </div>);

  }

  return (
    <form onSubmit={handleSubmit} className={`flex flex-col gap-3 ${className}`}>
      <Input
        type="email"
        required
        placeholder="your@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={status === "loading"}
        className="flex-1 h-14 text-center" />
      
      <Button
        type="submit"
        disabled={status === "loading"}
        size="lg"
        className="w-full sm:w-auto text-sm sm:text-base h-auto min-h-[48px] px-6 sm:px-8 py-3 rounded-full shadow-lg hover:shadow-xl hover:opacity-90 transition-shadow text-center leading-snug whitespace-normal"
        style={{ backgroundColor: variant === 'light' ? '#f7f4f0' : '#141318', color: variant === 'light' ? '#141318' : '#f7f4f0' }}>
        
        <span>{status === "loading" ? "Joining…" : buttonText}</span>
        {status !== "loading" && <ArrowRight className="h-4 w-4 ml-2 shrink-0 inline-block" />}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        By joining, you agree to our{" "}
        <Link to="/legal/privacypolicy" className="underline hover:text-foreground transition-colors">Privacy Policy</Link>.
      </p>
      {status === "error" &&
      <p className="text-sm text-destructive sm:col-span-2">{errorMsg}</p>
      }
    </form>);

}

/* ------------------------------------------------------------------ */
/*  FAQ SECTION                                                        */
/* ------------------------------------------------------------------ */

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return (
    <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: "#f7f4f0" }} aria-labelledby="faq-title">
      <div className="mx-auto max-w-2xl">
        <h2 id="faq-title" className="font-serif text-3xl md:text-5xl font-bold text-center mb-14" style={{ color: "#1a1a1a" }}>
          A few things you might be wondering
        </h2>
        {FAQ_ITEMS.map((item, i) =>
        <div key={i} style={{ borderBottom: "1px solid #e5e2dd" }}>
            <button
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            className="w-full flex justify-between items-center py-5 text-left font-semibold hover:opacity-70 transition-opacity"
            style={{ color: "#111" }}>
            
              <span>{item.q}</span>
              <ChevronDown
              className="w-5 h-5 shrink-0 ml-4 transition-transform duration-200"
              style={{ color: "#999", transform: openIndex === i ? "rotate(180deg)" : "rotate(0deg)" }} />
            
            </button>
            {openIndex === i &&
          <p className="leading-relaxed pb-5" style={{ color: "#888" }}>{item.a}</p>
          }
          </div>
        )}
      </div>
    </section>);

}

/* ------------------------------------------------------------------ */
/*  CLAIM MODAL                                                        */
/* ------------------------------------------------------------------ */

function ClaimModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Auto-close after success
  useEffect(() => {
    if (!submitted) return;
    const t = setTimeout(onClose, 2400);
    return () => clearTimeout(t);
  }, [submitted, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(20, 19, 24, 0.45)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl p-8 shadow-2xl"
        style={{ backgroundColor: "#f7f4f0" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {submitted ? (
          <div className="text-center py-4">
            <p className="font-serif text-2xl font-bold" style={{ color: "#141318" }}>You're in. ✨</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Your founding spot is reserved — £27/year, locked in for life.
            </p>
          </div>
        ) : (
          <>
            <h2 className="font-serif text-2xl font-bold mb-2" style={{ color: "#141318" }}>
              Claim your founding spot
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              £27/year, locked in for life. Just enter your email.
            </p>
            <WaitlistForm
              buttonText="Claim my spot"
              onSuccess={() => setSubmitted(true)}
            />
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  WAITLIST PAGE                                                      */
/* ------------------------------------------------------------------ */

export default function Waitlist() {
  const [spotsRemaining, setSpotsRemaining] = useState<number | null>(null);
  const [claimOpen, setClaimOpen] = useState(false);

  useEffect(() => {
    async function fetchCount() {
      const { data, error } = await supabase.functions.invoke("waitlist-count");
      const count = typeof data?.count === "number" ? data.count : null;
      if (!error && count !== null) {
        setSpotsRemaining(Math.max(0, Math.min(100, 100 - count)));
      }
    }
    fetchCount();
  }, []);

  const claimed = spotsRemaining !== null ? 100 - spotsRemaining : null;
  const spotsText = spotsRemaining !== null ? String(spotsRemaining) : "…";
  const claimedText = claimed !== null ? String(claimed) : "…";

  return (
    <main id="content" className="overflow-x-hidden">
      <SEO
        title="Money Management App for Women | WealthTimeline"
        description="A mindful budgeting app designed for women. Get early access, founding member pricing, and a calmer relationship with your money."
        path="/" />
      

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }} />
      

      {/* STICKY NAV BAR — outside sections so sticky works globally */}
      <header className="fixed inset-x-0 top-0 z-[110] px-4 py-3 backdrop-blur-md" style={{ backgroundColor: "rgba(238, 234, 228, 0.88)", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link to="/" className="font-semibold text-lg text-foreground hover:text-primary transition-colors shrink-0">
            {APP_NAME}
          </Link>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="hidden sm:inline">100 founding spots · £27/year locked in for life ·</span>
            <button onClick={() => setClaimOpen(true)} className="inline-flex items-center gap-1 font-semibold text-foreground hover:opacity-70 transition-opacity whitespace-nowrap">
              Claim yours <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </header>

      <ClaimModal open={claimOpen} onClose={() => setClaimOpen(false)} />

      {/* ============================================================ */}
      {/* S1 — HERO                                                     */}
      {/* ============================================================ */}
      <section
        className="relative overflow-hidden"
        aria-labelledby="hero-title"
        style={{ backgroundColor: "#eeeae4" }}>
        
        {/* Mesh gradient blobs — matching Landing */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
          <div style={{
            position: "absolute", width: "65%", height: "130%", top: "-15%", left: "-20%",
            background: "radial-gradient(ellipse, rgba(160, 100, 210, 0.60) 0%, transparent 70%)",
            filter: "blur(60px)"
          }} />
          <div style={{
            position: "absolute", width: "65%", height: "130%", top: "-15%", right: "-20%",
            background: "radial-gradient(ellipse, rgba(225, 130, 95, 0.55) 0%, transparent 70%)",
            filter: "blur(60px)"
          }} />
          <div style={{
            position: "absolute", width: "50%", height: "60%", bottom: "-10%", left: "25%",
            background: "radial-gradient(ellipse, rgba(210, 160, 180, 0.25) 0%, transparent 70%)",
            filter: "blur(50px)"
          }} />
          <div style={{
            position: "absolute", inset: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            opacity: 0.08, mixBlendMode: "overlay" as const
          }} />
        </div>

        <div className="relative z-10 mx-auto max-w-6xl px-6 pt-28 pb-24 sm:pt-36 sm:pb-32 flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="text-center md:text-left md:max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-6">
              Launching 11 November 2026 · 100 Founding Member spots available
            </p>
            <h1
              id="hero-title"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-foreground mb-8">
              
              The mindful money system that finally gets it — and gets you.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto md:mx-0 mb-10 leading-relaxed">
              You earn. You try. But somehow the money still slips through your fingers — 
              and nobody has ever handed you a clear, calm, shame-free plan for where you <em>actually</em> are right now.
            </p>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto md:mx-0 mb-10 leading-relaxed">
              WealthTimeline is a mindful money operating system built for women. 
              It launches on 11 November 2026 — and the first 100 women to join lock in a 
              founding member rate of £27/year. Forever.
            </p>

            <div id="hero-cta">
              <WaitlistForm className="max-w-lg mx-auto md:mx-0" buttonText="Claim my founding spot — £27/year, locked in for life" />
            </div>

            <p className="mt-5 text-sm text-muted-foreground">
              Waitlist now open — founding member spots filling fast · Your data is never shared or sold · Unsubscribe any time
              {/* {claimedText} of 100 founding spots claimed · Your data is never shared or sold · Unsubscribe any time */}
            </p>
          </div>
          <MockupImage
            src="/images/app-mockup-dashboard.png"
            alt="WealthTimeline dashboard showing your money stage and goals"
            className="w-full max-w-sm mx-auto md:mx-0 mt-12 md:mt-0 drop-shadow-2xl rounded-[2.5rem]"
            style={{ transform: 'rotate(2deg)' }}
          />
        </div>
      </section>

      {/* ============================================================ */}
      {/* S2 — THE HOOK                                                 */}
      {/* ============================================================ */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: "#1a1a1a" }}>
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-snug italic">
            "I make decent money. So why don't I feel wealthy?"
          </p>
          <p className="mt-6 text-lg" style={{ color: "#999" }}>
            If that thought has ever crossed your mind — even once — this was built for you.
          </p>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S3 — WHAT YOU'RE WAITING FOR                                  */}
      {/* ============================================================ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: "#f7f4f0" }} aria-labelledby="features-title">
        <div className="mx-auto max-w-6xl">
          <h2 id="features-title" className="font-serif text-3xl md:text-5xl font-bold text-center mb-4" style={{ color: "#1a1a1a" }}>
            This isn't another budgeting app.
          </h2>
          <p className="text-lg text-center max-w-2xl mx-auto mb-14 leading-relaxed" style={{ color: "#888" }}>
            Most money apps give you charts. WealthTimeline gives you <em>clarity</em>.
            At the heart of everything is a simple framework — Wants, Needs, Freedom™ — 
            that makes every financial decision instantly legible. 
            It places you in your personal money stage — 
            Seed Planter, Budget Builder, Wealth Grower, or Financially Free — 
            and gives you one clear, calm next step. No shame. No overwhelm. 
            No advice designed for someone else's life.
          </p>
          <p className="text-sm uppercase tracking-widest text-center mb-10" style={{ color: "#999" }}>
            What you get from day one
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FEATURES.map(({ Icon, iconBg, iconColor, title, body }) => (
              <div key={title} className="rounded-2xl p-8 shadow-sm" style={{ backgroundColor: '#ffffff' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: iconBg }}>
                  <Icon className="w-5 h-5" style={{ color: iconColor }} />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#111' }}>{title}</h3>
                <p className="leading-relaxed" style={{ color: '#888' }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* MOCKUP SHOWCASE — goals iPad                                   */}
      {/* ============================================================ */}
      <div className="w-full bg-transparent py-8 md:py-12">
        <p className="text-xs font-medium tracking-widest uppercase text-center mb-6" style={{ color: '#999' }}>
          See it in action
        </p>
        <div className="relative w-full max-w-2xl mx-auto px-6">
          <MockupImage
            src="/images/app-mockup-goals.png"
            alt="WealthTimeline goals tracker"
            className="w-full"
            style={{ transform: 'rotate(-2deg)' }}
          />
        </div>
        <p className="text-sm text-center mt-6 max-w-sm mx-auto" style={{ color: '#999' }}>
          Your goals, your timeline — tracked in one calm view.
        </p>
      </div>

      {/* ============================================================ */}
      {/* S4 — WHO THIS IS FOR                                          */}
      {/* ============================================================ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: "#ffffff" }} aria-labelledby="built-for-title">
        <div className="mx-auto max-w-3xl">
          <h2 id="built-for-title" className="font-serif text-3xl md:text-5xl font-bold text-center mb-14" style={{ color: "#1a1a1a" }}>
            Built for you if…
          </h2>
          <ul className="space-y-5">
            {BUILT_FOR_YOU.map((item, i) =>
            <li key={i} className="flex items-start gap-4">
                <span className="mt-1 w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#f0fdf4" }}>
                  <Check className="w-3.5 h-3.5" style={{ color: "#16a34a" }} />
                </span>
                <span className="text-lg leading-relaxed" style={{ color: "#555" }}>{item}</span>
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S5 — EARLY ACCESS OFFER                                       */}
      {/* ============================================================ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: "#f7f4f0" }} aria-labelledby="early-access-title">
        <div className="mx-auto max-w-3xl">
          <h2 id="early-access-title" className="font-serif text-3xl md:text-5xl font-bold text-center mb-4" style={{ color: "#1a1a1a" }}>
            The founding rate is for the first 100 women. After that, it's gone forever.
          </h2>
          <p className="text-lg text-center max-w-2xl mx-auto mb-12 leading-relaxed" style={{ color: "#888" }}>
            This isn't just a place in a queue. The first 100 women to join WealthTimeline become founding members — and founding members get something nobody else ever will.
          </p>
          <div className="space-y-4 mb-12">
            {EARLY_ACCESS_PERKS.map((perk, i) =>
            <div key={i} className="flex items-start gap-4 rounded-2xl p-5 shadow-sm" style={{ backgroundColor: "#ffffff" }}>
                <span className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#f0fdf4" }}>
                  <Check className="w-3.5 h-3.5" style={{ color: "#16a34a" }} />
                </span>
                <span className="leading-relaxed" style={{ color: "#555" }}>{perk}</span>
              </div>
            )}
          </div>
          <p className="text-center text-sm italic mb-8" style={{ color: "#888" }}>
            After the first 100 spots, the price moves to £47/year — and founding member pricing closes permanently.
          </p>
          <p className="text-center text-sm italic font-bold mb-8" style={{ color: "#888" }}>
            Waitlist now open — founding member spots filling fast.
            {/* {spotsText} of 100 founding spots remaining. */}
          </p>
          <WaitlistForm className="max-w-lg mx-auto" buttonText="Claim my founding spot — £27/year, locked in for life" />
        </div>
      </section>

      {/* ============================================================ */}
      {/* S6 — HOW IT WORKS                                             */}
      {/* ============================================================ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: "#ffffff" }} aria-labelledby="how-title">
        <div className="mx-auto max-w-6xl">
          <h2 id="how-title" className="font-serif text-3xl md:text-5xl font-bold text-center mb-4" style={{ color: "#1a1a1a" }}>
            What happens when you join
          </h2>
          <p className="text-lg text-center max-w-2xl mx-auto mb-14" style={{ color: "#888" }}>&nbsp;</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS_STEPS.map((step) =>
            <div key={step.num} className="rounded-2xl p-8 shadow-sm" style={{ backgroundColor: "#f9f8f6" }}>
                <span className="text-7xl font-bold leading-none mb-4 block" style={{ color: "#eae8e4" }}>{step.num}</span>
                <h3 className="text-xl font-semibold mb-3" style={{ color: "#111" }}>{step.title}</h3>
                <p className="leading-relaxed" style={{ color: "#888" }}>{step.body}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S7 — FOUNDER NOTE                                             */}
      {/* ============================================================ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: "#ffffff" }} aria-labelledby="founder-title">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-widest mb-6" style={{ color: "#999" }}>
                Why I built this
              </p>
              <div className="space-y-5" style={{ color: "#555" }}>
                <p className="leading-relaxed">
                  Nobody taught me about money growing up. My parents were immigrants — 
                  they were too busy making ends meet to sit me down and explain any of it.
                </p>
                <p className="leading-relaxed">
                  I'm now a senior tech consultant and a mum of two young daughters. 
                  I spent years learning the hard way what nobody ever taught me. 
                  And when I looked around, I saw so many women in exactly the same place — 
                  smart, capable, earning well, but still feeling behind. 
                  Not because they were failing. Because they'd never had the right system, 
                  in the right language, built for where they <em>actually</em> were.
                </p>
                <p className="leading-relaxed">
                  WealthTimeline is what I wish I'd had ten years ago. I can't wait for you to use it.
                </p>
              </div>
              <p className="mt-8 font-semibold" style={{ color: "#111" }}>
                — Raluca, Founder of WealthTimeline
              </p>
            </div>
            <div className="rounded-2xl aspect-square w-full overflow-hidden">
              <img src="/images/founder-photo.jpg" alt="Raluca, Founder of WealthTimeline" className="w-full h-full object-cover rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S8 — SOCIAL PROOF PLACEHOLDER                                 */}
      {/* ============================================================ */}
      <section className="py-16" style={{ backgroundColor: "#1a1a1a" }}>
        <div className="mx-auto max-w-4xl px-6">
          <div className="flex flex-wrap justify-center gap-4 text-sm mb-12" style={{ color: "#999" }}>
            <span>Built after 200+ conversations. Now it's your turn.</span>
            {/* <span>{claimedText} women already on the waitlist</span> */}
            {/* <span className="opacity-40">·</span> */}
            {/* <span>Waitlist opened March 2026</span> */}
          </div>
          <div className="max-w-xl mx-auto text-center rounded-2xl p-8" style={{ backgroundColor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <p className="text-lg text-white leading-relaxed mb-4">
              ✦ No reviews yet — and that's the point.
            </p>
            <p className="text-lg text-white leading-relaxed mb-4">
              WealthTimeline hasn't launched yet. 
              The women joining the waitlist right now aren't following the crowd — they're <em>leading</em> it.
            </p>
            <p className="text-lg text-white leading-relaxed mb-4">
              If you join today, yours could be the first voice here.
            </p>
            <button onClick={() => setClaimOpen(true)} className="text-sm cursor-pointer hover:opacity-80 transition-opacity" style={{ color: "#999" }}>
              → Claim your founding spot
            </button>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S9 — FAQ                                                      */}
      {/* ============================================================ */}
      <FAQSection />

      {/* ============================================================ */}
      {/* S10 — FINAL CTA                                               */}
      {/* ============================================================ */}
      <section className="py-24" style={{ backgroundColor: "#1a1a1a" }}>
        <div className="mx-auto max-w-2xl px-6 text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-white text-center mb-6 max-w-2xl mx-auto">
            You've been meaning to sort your money for a while.
          </h2>
          <p className="text-lg text-center mb-10 max-w-xl mx-auto" style={{ color: "#999" }}>
            This is the calm, clear place to start.
          </p>
          <p className="text-lg text-center mb-10 max-w-xl mx-auto" style={{ color: "#999" }}>           
            Right now, being early has a real price tag on it — <strong>£27/year, 
            locked in for life</strong>, before the founding 100 spots are gone.
          </p>
          <WaitlistForm className="max-w-lg mx-auto" buttonText="Claim my founding spot — £27/year, locked in for life" variant="light" />
          <p className="text-sm mt-5 text-center" style={{ color: "#666" }}>
            Waitlist now open — founding member spots filling fast
            {/* {spotsText} of 100 founding spots remaining · No credit card · No spam · Launching 11 November 2026 */}
          </p>
          <p className="text-sm mt-5 text-center" style={{ color: "#666" }}>
            No credit card · No spam · Launching 11 November 2026
          </p>
        </div>
      </section>
      {/* Footer */}
      <footer className="py-10 border-t border-white/10" style={{ backgroundColor: "#1a1a1a" }}>
        <div className="mx-auto max-w-4xl px-6 flex flex-col items-center gap-4">
          <p className="text-xs" style={{ color: "#555" }}>
            © {new Date().getFullYear()} WealthTimeline. All rights reserved.
          </p>
        </div>
      </footer>
    </main>);

}